/* @typescript-eslint/ban-ts-ignore */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import createCarbonApi from 'api/carbonOffset';
import createFlightApi, { getFlightOffsetWithFlightNumber } from 'api/flightOffset';
import createOrderApi, { Metadata } from 'api/order';
import { format } from 'date-fns';
import { currentUserSelector } from 'modules/user/userSlice';
import { showToast } from 'modules/app/toasterSlice';
import { syncAttribution } from 'modules/user/attributionSlice';
import { AppThunk } from 'store';
import calculateMiles from 'utils/calculateMiles';
import { appContextSelector } from 'modules/app/appSlice';
import { defaultPortfolioSelector } from 'modules/projects/projectsSlice';
import { setTag, tags } from 'utils/tags';

interface OffsetPrice {
  price: number;
  currency: string;
  vat?: number;
}

interface AirportsParams {
  from?: AirportOption;
  to?: AirportOption;
}

interface OrderPayload {
  number: string;
  date: string;
}

interface State {
  offsetSubType: string;
  data?: FlightOffsetParams;
  roundTrip: boolean;
  useMiles: boolean;
  amount?: number;
  isLoading: boolean;
  orderNumber?: string;
  orderDate?: string;
  chosenFlight: FlightOffset | null;
  error: boolean;
  price?: number;
  currency?: string;
  flightNumberSummary: boolean;
  airportFrom?: AirportOption;
  airportTo?: AirportOption;
  vat?: number;
}

const initialState: State = {
  isLoading: false,
  useMiles: false,
  roundTrip: false,
  offsetSubType: 'fromTo',
  chosenFlight: null,
  flightNumberSummary: false,
  error: false,
};

const slice = createSlice({
  name: 'offsetFlightPersonal',
  initialState,
  reducers: {
    reset: (): State => {
      return { ...initialState };
    },
    setPrice: (state: State, action: PayloadAction<OffsetPrice>): State => {
      state.price = action.payload.price;
      state.currency = action.payload.currency;
      state.vat = action.payload.vat;
      return state;
    },
    setOffsetSubType: (state: State, action: PayloadAction<string>): State => {
      state.offsetSubType = action.payload;
      return state;
    },
    setRoundTrip: (state: State, action: PayloadAction<boolean>): State => {
      state.roundTrip = action.payload;
      return state;
    },
    setMiles: (state: State, action: PayloadAction<boolean>): State => {
      state.useMiles = action.payload;
      return state;
    },
    setAmount: (state: State, action: PayloadAction<number>): State => {
      state.amount = action.payload;
      return state;
    },
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.isLoading = action.payload;
      return state;
    },
    clearFlight: (state: State): State => {
      state.flightNumberSummary = false;
      state.chosenFlight = null;
      return state;
    },
    clearErrors: (state: State): State => {
      state.error = false;
      return state;
    },
    setError: (state: State, action: PayloadAction<boolean>): State => {
      state.error = action.payload;
      return state;
    },
    setChosenFlight: (state: State, action: PayloadAction<FlightOffset>): State => {
      state.chosenFlight = action.payload;
      return state;
    },
    setFlightNumberSummary: (state: State, action: PayloadAction<boolean>): State => {
      state.flightNumberSummary = action.payload;
      return state;
    },
    setFlightData: (state: State, action: PayloadAction<Partial<FlightOffsetParams>>): State => {
      state.data = action.payload;
      return state;
    },
    setAirports: (state: State, action: PayloadAction<AirportsParams>): State => {
      state.airportFrom = action.payload.from;
      state.airportTo = action.payload.to;
      return state;
    },
    setOrder: (state: State, action: PayloadAction<OrderPayload>): State => {
      state.orderNumber = action.payload.number;
      state.orderDate = action.payload.date;
      return state;
    },
  },
});

const {
  reset,
  setOffsetSubType,
  setRoundTrip,
  setMiles,
  clearFlight,
  clearErrors,
  setLoading,
  setError,
  setChosenFlight,
  setAmount,
  setPrice,
  setFlightNumberSummary,
  setFlightData,
  setAirports,
  setOrder,
} = slice.actions;
export { reset, setOffsetSubType, setRoundTrip, setMiles, clearFlight, clearErrors };
export default slice.reducer;

function generateMetadata(flight: State): Metadata {
  let flightMetadata;

  if (flight.offsetSubType === 'fromTo' || flight.offsetSubType === 'flightNumber') {
    flightMetadata = {
      travelersCount: Number(flight.data?.travellers ?? 1),
      roundtrip: flight.roundTrip,
      distanceKm: flight.data?.distanceKm,
      fromAirport: flight.airportFrom?.airportCode ?? '',
      toAirport: flight.airportTo?.airportCode ?? '',
      fromCountry: flight.airportFrom?.countryCode ?? '',
      toCountry: flight.airportTo?.countryCode ?? '',
      fromCity: flight.airportFrom?.city ?? '',
      toCity: flight.airportTo?.city ?? '',
      flights: 1,
      passengers: Number(flight.data?.travellers ?? 1),
      travelClass: flight.data?.travelClass ?? ('economy' as TravelClass),
    };
  } else {
    flightMetadata = {
      travelersCount: Number(flight.data?.travellers ?? 1),
      distanceKm: flight.data?.distanceKm,
      roundtrip: flight.roundTrip,
      flights: 1,
      passengers: Number(flight.data?.travellers ?? 1),
    };
  }

  return {
    //@ts-ignore
    flight: flightMetadata,
    order: {
      channel: 'mychooose-fulloffset',
    },
  };
}

export const calculateOffsetWithFlightNumber = (params: Partial<FlightOffsetParams>): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const { roundTrip } = getState().flightOffsetPersonal;

  try {
    let newDate = null;

    if (params.flightDate) {
      const splitDate = params.flightDate.split('/');
      newDate = format(new Date(parseInt(splitDate[2]), parseInt(splitDate[1]) - 1, parseInt(splitDate[0])), 'yyyyMMdd');
    }

    const response = await getFlightOffsetWithFlightNumber({
      flightNumber: params.flightNumber ?? '',
      date: newDate,
      roundTrip,
      passengers: params.travellers ?? 1,
      travelClass: params.travelClass ?? 'economy',
    });

    dispatch(setChosenFlight(response));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setError(true));
    dispatch(setLoading(false));
  }
};

export const proceedOffsetWithFlightNumber = (): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const { roundTrip, chosenFlight } = getState().flightOffsetPersonal;
  const defaultPortfolio = defaultPortfolioSelector(getState());

  if (!chosenFlight) {
    return;
  }

  dispatch(setFlightNumberSummary(true));
  dispatch(setLoading(true));

  try {
    const priceData = await carbonApi.getCarbonOffsetPrice(
      { kilosCo2: chosenFlight.kilosCo2, flightPax: (chosenFlight.passengers ?? 1) * (roundTrip ? 2 : 1) },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id,
    );

    dispatch(
      setPrice({
        price: priceData.salesPrice,
        currency: priceData.salesCurrency,
        vat: priceData.vat,
      }),
    );
    dispatch(setAmount(chosenFlight.kilosCo2));
    dispatch(
      setFlightData({
        ...chosenFlight,
        travellers: chosenFlight.passengers ?? 1,
        distance: chosenFlight.distanceKm,
        travelClass: (chosenFlight.travelClass ?? 'economy') as TravelClass,
      }),
    );
    dispatch(
      setAirports({
        from: {
          label: chosenFlight.flight.fromAirport ?? '',
          value: chosenFlight.flight.fromAirport ?? '',
          country: chosenFlight.flight.fromCountryDetails.official_name_en ?? '',
          city: chosenFlight.flight.fromAirportDetails?.municipality ?? '',
          airportCode: chosenFlight.flight.fromAirport ?? '',
          countryCode: chosenFlight.flight.fromAirportDetails?.iso_country ?? '',
        },
        to: {
          label: chosenFlight.flight.toAirport ?? '',
          value: chosenFlight.flight.toAirport ?? '',
          country: chosenFlight.flight.toCountryDetails.official_name_en ?? '',
          city: chosenFlight.flight.toAirportDetails?.municipality ?? '',
          airportCode: chosenFlight.flight.toAirport ?? '',
          countryCode: chosenFlight.flight.toAirportDetails?.iso_country ?? '',
        },
      }),
    );
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setError(true));
    dispatch(setLoading(false));
  }
};

export const calculateOffsetByDistance = (params: Partial<FlightOffsetParams>): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const flightApi = createFlightApi(context);
  const { roundTrip, useMiles } = getState().flightOffsetPersonal;
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const response = await flightApi.getFlightOffset({
      distance: Math.round(useMiles ? calculateMiles(params.flightLength ?? 1) : params.flightLength ?? 1),
      roundTrip: roundTrip ?? false,
      travellers: params.travellers ?? 1,
      travelClass: (params.travelClass ?? 'economy') as TravelClass,
    });

    const priceData = await carbonApi.getCarbonOffsetPrice(
      { kilosCo2: response.kilosCo2, flightPax: (params.travellers ?? 1) * (roundTrip ? 2 : 1) },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id,
    );

    dispatch(
      setPrice({
        price: priceData.salesPrice,
        currency: priceData.salesCurrency,
        vat: priceData.vat,
      }),
    );
    dispatch(setAmount(response.kilosCo2));
    dispatch(
      setFlightData({
        ...params,
        distance: useMiles ? response.distanceMiles : response.distanceKm,
        travelClass: (params.travelClass ?? 'economy') as TravelClass,
      }),
    );
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
  }
};

export const calculateOffsetFromTo = (params: Partial<FlightOffsetParams>, airports: AirportsParams): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const flightApi = createFlightApi(context);
  const defaultPortfolio = defaultPortfolioSelector(getState());

  const { roundTrip } = getState().flightOffsetPersonal;

  try {
    const response = await flightApi.getFlightOffset({
      from: airports.from?.value ?? '',
      to: airports.to?.value ?? '',
      roundTrip: roundTrip ?? false,
      travellers: params.travellers ?? 1,
      travelClass: (params.travelClass ?? 'economy') as TravelClass,
    });

    const priceData = await carbonApi.getCarbonOffsetPrice(
      { kilosCo2: response.kilosCo2, flightPax: (params.travellers ?? 1) * (roundTrip ? 2 : 1) },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id,
    );

    dispatch(
      setPrice({
        price: priceData.salesPrice,
        currency: priceData.salesCurrency,
        vat: priceData.vat,
      }),
    );
    dispatch(
      setAirports({
        from: airports.from,
        to: airports.to,
      }),
    );
    dispatch(setAmount(response.kilosCo2));
    dispatch(setFlightData({ ...params, distance: response.distanceKm, travelClass: (params.travelClass ?? 'economy') as TravelClass }));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
  }
};

export const createOrder = (errorCallback: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const orderApi = createOrderApi(context);
  const { currency, amount, price, data, roundTrip, airportTo, airportFrom } = getState().flightOffsetPersonal;
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const metadata = generateMetadata(getState().flightOffsetPersonal);

    const orderResult = await orderApi.create(
      {
        description: '',
        comment: '',
        currency: currency ?? '',
        category: 'Flights',
        items: [
          {
            type: 'impact',
            impact: 'Co2',
            kilosCo2: amount ?? 0,
            price: price ?? 0,
            portfolioId: defaultPortfolio?.portfolioData.id ?? 'defaultco2',
            details: {
              flight: {
                flights: 1,
                passengers: Number(data?.travellers ?? 0),
                roundTrip,
                travelClass: data?.travelClass ?? ('economy' as TravelClass),
                routeLegs: [
                  {
                    distanceKm: data?.distanceKm ?? 0,
                    fromAirportCode: airportFrom?.airportCode ?? '',
                    toAirportCode: airportTo?.airportCode ?? '',
                    fromCountryName: airportFrom?.countryCode ?? '',
                    toCountryName: airportTo?.countryCode ?? '',
                    fromCityName: airportFrom?.city ?? '',
                    toCityName: airportTo?.city ?? '',
                  },
                ],
              },
            },
            feeFactors: {
              flightPax: Number(data?.travellers ?? 1) * (roundTrip ? 2 : 1),
              flight: true,
            },
          },
        ],
        payment: {
          type: 'invoice',
        },
        metadata,
      },
      currentUser?.customerId ?? '',
    );
    setTag({
      ...tags.portalOffset.placeOrder,
      categories: [orderResult.order.category],
      transactionId: orderResult.orderId,
      transactionTotal: orderResult.order.totalPrice,
      currencyCode: orderResult.order.currency,
      transactionProducts: orderResult.order.items.map(item => ({
        category: orderResult.order.category,
        name: 'Flight offset',
        price: item.price,
        quantity: item.kilosCo2,
        sku: 'flight-offset',
      })),
    });

    dispatch(
      setOrder({
        number: orderResult.order.choooseId,
        date: orderResult.order.createdDate,
      }),
    );

    dispatch(syncAttribution());
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
    errorCallback();
  }
};
